// src/utils/format.ts
import moment, { MomentInput } from 'moment';
import 'moment/locale/fr';
import { SHOP_INFO_STORAGE_KEY } from '@/config/app';
import { currencyDecimalPlaces } from '@/utils/money';

// On fixe la locale en français
moment.locale('fr');

export interface FormatUtils {
  date(value: MomentInput | null | undefined, timezone?: string | null): string;
  datetime(value: MomentInput | null | undefined, timezone?: string | null): string;
  time(value: MomentInput | null | undefined, timezone?: string | null): string;
  currency(
    value: number | string | null | undefined,
    currency?: string
  ): string;
  longDate(value: MomentInput | null | undefined): string;
}

const currencyLabels: Record<string, string> = {
  XAF: 'FCFA',
  XOF: 'FCFA',
  FCFA: 'FCFA',
  EUR: '€',
  USD: '$',
};

interface AmountSettings {
  currency_code?: string;
  currency_position?: 'before' | 'after';
  decimal_places?: number;
  decimal_separator?: string;
  thousands_separator?: string;
}

const currentAmountSettings = (): AmountSettings => {
  if (typeof window === 'undefined') return { currency_code: 'FCFA' };

  try {
    const raw = window.localStorage.getItem(SHOP_INFO_STORAGE_KEY);
    const info = raw ? JSON.parse(raw) : null;
    return info || { currency_code: 'FCFA' };
  } catch {
    return { currency_code: 'FCFA' };
  }
};

const displayCurrency = (currency: string): string => {
  const code = (currency || 'FCFA').toUpperCase();
  return currencyLabels[code] || code;
};

const normalizeThousandsSeparator = (value: unknown): string => {
  if (value === 'space') return ' ';
  if (value === 'dot') return '.';
  if (value === 'comma') return ',';
  if (value === 'none') return '';
  if (value === null || value === undefined) return ' ';

  return String(value);
};

const formatAmount = (value: number, settings: AmountSettings): string => {
  const decimalPlaces = currencyDecimalPlaces(settings);
  const decimalSeparator = settings.decimal_separator === '.' ? '.' : ',';
  const thousandsSeparator = normalizeThousandsSeparator(settings.thousands_separator);
  const fixed = value.toFixed(decimalPlaces);
  const [integerPart, decimalPart] = fixed.split('.');
  const sign = integerPart.startsWith('-') ? '-' : '';
  const unsignedInteger = sign ? integerPart.slice(1) : integerPart;
  const grouped = unsignedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return `${sign}${grouped}${decimalPlaces > 0 ? decimalSeparator + decimalPart : ''}`;
};

const formatInTimezone = (
  value: MomentInput,
  timezone: string,
  options: Intl.DateTimeFormatOptions
): string | null => {
  const date = moment(value).toDate();
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat('fr-FR', {
      ...options,
      timeZone: timezone,
    }).format(date).replace(',', '');
  } catch {
    return null;
  }
};

const format: FormatUtils = {
  /**
   * Format date simple : 14/02/2025
   */
  date(value, timezone) {
    if (!value) return '';
    if (timezone) {
      return formatInTimezone(value, timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }) || moment(value).format('DD/MM/YYYY');
    }
    return moment(value).format('DD/MM/YYYY');
  },

  /**
   * Format date + heure : 14/02/2025 13:45
   */
  datetime(value, timezone) {
    if (!value) return '';
    if (timezone) {
      return formatInTimezone(value, timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }) || moment(value).format('DD/MM/YYYY HH:mm');
    }
    return moment(value).format('DD/MM/YYYY HH:mm');
  },

  /**
   * Format heure seule : 13:45
   */
  time(value, timezone) {
    if (!value) return '';
    if (timezone) {
      return formatInTimezone(value, timezone, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }) || moment(value).format('HH:mm');
    }
    return moment(value).format('HH:mm');
  },

  /**
   * Format montant FCFA (ou autre devise).
   * Exemple : 15000 → "15 000 FCFA"
   */
  currency(value, currency) {
    const settings = currentAmountSettings();
    const currencyLabel = displayCurrency(currency || settings.currency_code || 'FCFA');

    if (value === null || value === undefined || value === '') {
      const zero = formatAmount(0, settings);
      return settings.currency_position === 'before' ? `${currencyLabel} ${zero}` : `${zero} ${currencyLabel}`;
    }

    const numeric = typeof value === 'number' ? value : Number(value);

    if (Number.isNaN(numeric)) {
      const zero = formatAmount(0, settings);
      return settings.currency_position === 'before' ? `${currencyLabel} ${zero}` : `${zero} ${currencyLabel}`;
    }

    const amount = formatAmount(numeric, settings);
    return settings.currency_position === 'before'
      ? `${currencyLabel} ${amount}`
      : `${amount} ${currencyLabel}`;
  },

  /**
   * Date longue pour rapports : "14 février 2025"
   */
  longDate(value) {
    if (!value) return '';
    return moment(value).format('D MMMM YYYY');
  },
};

export default format;
